/**
 * VisibilityToggle — small inline widget that appears on Family Tree
 * and Memorial pages. Behaviour depends on the viewer:
 *
 *   1. Anonymous visitor on a PUBLIC page → soft callout:
 *        "Are you the owner? Sign in to make this private."
 *      Clicking the CTA routes to /register?redirect=<current-path>
 *      so the user returns here after auth.
 *
 *   2. Authenticated viewer who is NOT the owner/admin → nothing.
 *
 *   3. Authenticated OWNER/ADMIN → a compact toggle:
 *        [ Public ⏵ ] / [ Private ⏵ ]
 *      with a caption clarifying that Private = owner + invited
 *      members + explicit access grants can view.
 *
 * Props:
 *   kind          "family-tree" | "memorial"
 *   currentValue  "public" | "private" | ...   (current backend value)
 *   canManage     boolean — true when the current user is the tree
 *                  admin/owner or memorial submitter (parent decides)
 *   isSignedIn    boolean
 *   onSaved       (newValue) => void
 *
 * Save handlers hit:
 *   family-tree → PATCH /api/family-trees/{tree_id} { visibility }
 *   memorial    → PATCH /api/memorials/{slug}/privacy { privacy }
 */
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock, Globe2, ShieldCheck, LogIn, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

const NORMALISE = {
  // Family Tree uses "public" | "invite" | "private"; we treat both
  // invite + private as "private" for the visitor-facing label because
  // both semantically mean "not open to the public".
  "public": "public",
  "invite": "private",
  "private": "private",
  // Memorials use "public" | "family" | "private"; same collapse.
  "family": "private",
};

export default function VisibilityToggle({
  kind, currentValue, canManage, isSignedIn, targetId, onSaved,
}) {
  const [busy, setBusy] = useState(false);
  const [value, setValue] = useState(NORMALISE[currentValue] || "public");
  const loc = useLocation();

  // Anonymous callout ----------------------------------------------------
  if (!isSignedIn) {
    if (value !== "public") return null;   // Not shown when already private
    const redirect = encodeURIComponent(loc.pathname + loc.search);
    return (
      <div
        className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 border text-xs bg-white/85 backdrop-blur"
        style={{ borderColor: "var(--wofy-border)" }}
        data-testid="visibility-toggle-anon-callout"
      >
        <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#4338CA" }} />
        <span className="opacity-80">
          Are you the {kind === "memorial" ? "poster" : "owner"} of this {kind === "memorial" ? "memorial" : "family tree"}? &nbsp;
        </span>
        <Link
          to={`/register?redirect=${redirect}`}
          className="font-semibold inline-flex items-center gap-1 hover:underline"
          style={{ color: "#4338CA" }}
          data-testid="visibility-toggle-signin-link"
        >
          <LogIn className="w-3 h-3" /> Sign in to make it private
        </Link>
      </div>
    );
  }

  // Authenticated non-owner → nothing ------------------------------------
  if (!canManage) return null;

  // Owner / admin toggle --------------------------------------------------
  const set = async (newVal) => {
    if (busy || newVal === value) return;
    setBusy(true);
    try {
      if (kind === "memorial") {
        await http.patch(`/memorials/${targetId}/privacy`, { privacy: newVal });
      } else {
        // Family tree — the API uses `visibility` and accepts "public" |
        // "invite" | "private". We only expose the two extremes here.
        await http.patch(`/family-trees/${targetId}`, { visibility: newVal });
      }
      setValue(newVal);
      onSaved?.(newVal);
      toast.success(
        newVal === "public"
          ? "Now public — anyone can view."
          : (kind === "memorial"
              ? "Now private — only you, followers and people you invite can view."
              : "Now private — only tree members and people you invite can view.")
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update visibility.");
    } finally { setBusy(false); }
  };

  const Pill = ({ v, Icon, label, tone }) => (
    <button
      type="button"
      onClick={() => set(v)}
      disabled={busy}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border transition ${value === v ? "font-semibold" : "opacity-70 hover:opacity-100"}`}
      style={value === v
        ? { borderColor: tone.border, color: tone.color, background: tone.bg }
        : { borderColor: "var(--wofy-border)" }}
      data-testid={`visibility-toggle-${v}`}
    >
      {busy && value !== v ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {label}
    </button>
  );

  return (
    <div
      className="mt-3 inline-flex flex-col items-start gap-1 rounded-xl px-3 py-2 border bg-white/85 backdrop-blur"
      style={{ borderColor: "var(--wofy-border)" }}
      data-testid="visibility-toggle-owner"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#4338CA" }} />
        <span className="text-xs font-medium">Who can see this?</span>
        <div className="flex items-center gap-1.5 ml-1">
          <Pill v="public"  Icon={Globe2} label="Public"  tone={{ border: "#059669", color: "#059669", bg: "#ECFDF5" }} />
          <Pill v="private" Icon={Lock}   label="Private" tone={{ border: "#DC2626", color: "#DC2626", bg: "#FEF2F2" }} />
        </div>
      </div>
      <div className="text-[10px] opacity-60 pl-5">
        {value === "public"
          ? "Anyone on the internet can view this page."
          : (kind === "memorial"
              ? "Only you, people you follow-in, and anyone you explicitly grant access can view."
              : "Only tree members and people you invite can view. Members always see it.")}
      </div>
    </div>
  );
}
